import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, IconButton, Select, MenuItem, FormControl,
  InputLabel, Chip, CircularProgress, Divider, Alert,
} from '@mui/material';
import {
  ArrowUpward, ArrowDownward, ArrowBack, ArrowForward,
  VolumeUp, VolumeDown, VolumeMute, Power, Home, Settings,
  Refresh, PlayArrow, RestartAlt,
} from '@mui/icons-material';
import Layout from '../../components/layout/Layout';
import { api } from '../../api/api';
import toast from 'react-hot-toast';

const DPAD_KEYS = {
  up: 'KEYCODE_DPAD_UP', down: 'KEYCODE_DPAD_DOWN',
  left: 'KEYCODE_DPAD_LEFT', right: 'KEYCODE_DPAD_RIGHT',
  ok: 'KEYCODE_DPAD_CENTER',
};

function TvRemote({ user }) {
  const [devices, setDevices] = useState([]);
  const [stores, setStores] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [deviceStatus, setDeviceStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    loadDevices();
    loadStores();
  }, []);

  const loadDevices = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/tvremote/devices');
      setDevices(data.devices || []);
    } catch { toast.error('디바이스 로드 실패'); }
    setLoading(false);
  };

  const loadStores = async () => {
    try {
      const { data } = await api.get('/tvremote/stores');
      setStores(data.stores || []);
    } catch {}
  };

  const connectDevice = async (device) => {
    setConnecting(true);
    setSelectedDevice(device);
    try {
      await api.post(`/tvremote/connect/${device._id}`);
      const { data } = await api.get(`/tvremote/status/${device._id}`);
      setDeviceStatus(data);
      toast.success(`${device.name} 연결됨`);
    } catch (e) {
      toast.error('연결 실패');
      setDeviceStatus(null);
    }
    setConnecting(false);
  };

  const sendKey = useCallback(async (keycode) => {
    if (!selectedDevice) return;
    try {
      navigator.vibrate?.(25);
      await api.post(`/tvremote/key/${selectedDevice._id}`, { keycode });
    } catch { toast.error('키 전송 실패'); }
  }, [selectedDevice]);

  const launchApp = async () => {
    if (!selectedDevice) return;
    try {
      await api.post(`/tvremote/launch/${selectedDevice._id}`);
      toast.success('Signage 앱 실행');
    } catch { toast.error('실행 실패'); }
  };

  const rebootDevice = async () => {
    if (!selectedDevice || !window.confirm('재부팅하시겠습니까?')) return;
    try {
      await api.post(`/tvremote/reboot/${selectedDevice._id}`);
      toast.success('재부팅 명령 전송');
    } catch { toast.error('재부팅 실패'); }
  };

  // D-Pad 버튼 스타일
  const dBtn = {
    width: 64, height: 64, borderRadius: 3,
    bgcolor: '#1e1e2e', border: '1px solid rgba(255,255,255,0.1)',
    color: '#fff', '&:active': { bgcolor: '#551FFF' },
  };
  const dOk = { ...dBtn, bgcolor: '#551FFF', fontWeight: 700, fontSize: 14 };

  return (
    <Layout title="TV Remote" user={user}>
      {/* CMS 서버에는 adb 가 없어 이 화면의 연결·리모컨은 아직 동작하지 않는다 (2026-11-02) — docs/REMOTE_SUPPORT_RUNBOOK.md */}
      <Alert severity="info" sx={{ mx: 2, mt: 2 }}>
        {(user?.role || user?.user?.role) === 'admin'
          ? <>이 화면의 원격 연결은 준비 중입니다. 원격 조작은 <a href="https://tools.digntec.com" target="_blank" rel="noopener noreferrer">tools.digntec.com</a> 에서, 앱 재시작·로그는 Devices → 기기 상세에서 하세요.</>
          : <>이 화면의 원격 리모컨은 준비 중입니다. TV 문제가 있으면 Digntec (201) 357-7222 로 연락 주세요 — 원격으로 확인해 드립니다.</>}
      </Alert>
      <Box sx={{ display: 'flex', gap: 2, p: 2, flexWrap: { xs: 'wrap', md: 'nowrap' }, minHeight: '80vh' }}>

        {/* 왼쪽: 디바이스 목록 */}
        <Paper sx={{ width: { xs: '100%', md: 280 }, p: 2, borderRadius: 3, flexShrink: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={700}>내 디바이스</Typography>
            <IconButton size="small" onClick={loadDevices}><Refresh fontSize="small" /></IconButton>
          </Box>

          {loading ? <CircularProgress size={24} /> : devices.length === 0 ? (
            <Typography color="text.secondary" fontSize={13}>등록된 디바이스가 없습니다.</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {devices.map(d => (
                <Paper
                  key={d._id}
                  onClick={() => connectDevice(d)}
                  sx={{
                    p: 1.5, cursor: 'pointer', borderRadius: 2,
                    border: selectedDevice?._id === d._id ? '2px solid #551FFF' : '1px solid rgba(255,255,255,0.08)',
                    '&:hover': { bgcolor: 'rgba(85,31,255,0.06)' },
                  }}
                >
                  <Typography fontWeight={600} fontSize={13}>{d.name}</Typography>
                  <Typography fontSize={11} color="text.secondary">
                    {d.store?.state_city || d.store?.name || '미배정'}
                  </Typography>
                  <Typography fontSize={11} fontFamily="monospace" color={d.wgIp ? '#4ade80' : '#888'}>
                    WG: {d.wgIp || '미할당'} {d.wgStatus === 'connected' ? '(연결됨)' : ''}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                    <Chip size="small" label={d.wgStatus || 'unknown'}
                      sx={{ fontSize: 10, height: 18,
                        bgcolor: d.wgStatus === 'connected' ? 'rgba(52,201,122,0.15)' : 'rgba(255,255,255,0.06)',
                        color: d.wgStatus === 'connected' ? '#34c97a' : '#888',
                      }} />
                    {d.provisioningStatus && (
                      <Chip size="small" label={d.provisioningStatus} sx={{ fontSize: 10, height: 18 }} />
                    )}
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </Paper>

        {/* 오른쪽: 리모컨 */}
        <Paper sx={{ flex: 1, p: 3, borderRadius: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {!selectedDevice ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography fontSize={48}>📺</Typography>
              <Typography variant="h6" color="text.secondary" mt={2}>디바이스를 선택하세요</Typography>
              <Typography fontSize={13} color="text.secondary">왼쪽 목록에서 제어할 TV를 선택합니다</Typography>
            </Box>
          ) : (
            <>
              {/* 디바이스 정보 */}
              <Box sx={{ textAlign: 'center', mb: 2, width: '100%' }}>
                <Typography fontWeight={700}>{selectedDevice.name}</Typography>
                <Typography fontSize={12} color="text.secondary">
                  {selectedDevice.wgIp} · {deviceStatus?.model || '—'} · Android {deviceStatus?.androidVersion || '—'}
                  {deviceStatus?.signageRunning && <Chip size="small" label="Signage 실행 중" sx={{ ml: 1, fontSize: 10, height: 18, bgcolor: 'rgba(52,201,122,0.15)', color: '#34c97a' }} />}
                </Typography>
              </Box>

              {connecting && <CircularProgress size={24} sx={{ mb: 2 }} />}

              {/* 컨트롤 버튼 */}
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <IconButton sx={dBtn} onClick={() => sendKey('KEYCODE_POWER')}><Power /></IconButton>
                <IconButton sx={dBtn} onClick={() => sendKey('KEYCODE_HOME')}><Home /></IconButton>
                <IconButton sx={dBtn} onClick={() => sendKey('KEYCODE_BACK')}><ArrowBack /></IconButton>
                <IconButton sx={dBtn} onClick={() => sendKey('KEYCODE_SETTINGS')}><Settings /></IconButton>
              </Box>

              {/* D-Pad */}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 64px)', gridTemplateRows: 'repeat(3, 64px)', gap: '6px', mb: 2 }}>
                <Box />
                <IconButton sx={dBtn} onClick={() => sendKey(DPAD_KEYS.up)}><ArrowUpward /></IconButton>
                <Box />
                <IconButton sx={dBtn} onClick={() => sendKey(DPAD_KEYS.left)}><ArrowBack /></IconButton>
                <IconButton sx={dOk} onClick={() => sendKey(DPAD_KEYS.ok)}>OK</IconButton>
                <IconButton sx={dBtn} onClick={() => sendKey(DPAD_KEYS.right)}><ArrowForward /></IconButton>
                <Box />
                <IconButton sx={dBtn} onClick={() => sendKey(DPAD_KEYS.down)}><ArrowDownward /></IconButton>
                <Box />
              </Box>

              {/* 볼륨 */}
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <IconButton sx={dBtn} onClick={() => sendKey('KEYCODE_VOLUME_DOWN')}><VolumeDown /></IconButton>
                <IconButton sx={dBtn} onClick={() => sendKey('KEYCODE_VOLUME_MUTE')}><VolumeMute /></IconButton>
                <IconButton sx={dBtn} onClick={() => sendKey('KEYCODE_VOLUME_UP')}><VolumeUp /></IconButton>
              </Box>

              <Divider sx={{ width: '100%', my: 2 }} />

              {/* 앱 실행 / 재부팅 */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="outlined" startIcon={<PlayArrow />} onClick={launchApp} sx={{ borderRadius: 2 }}>
                  Signage 실행
                </Button>
                <Button variant="outlined" color="error" startIcon={<RestartAlt />} onClick={rebootDevice} sx={{ borderRadius: 2 }}>
                  재부팅
                </Button>
              </Box>
            </>
          )}
        </Paper>
      </Box>
    </Layout>
  );
}

export default TvRemote;
